// V1.37.15 · Nombre completo corto de un alumno.
// Formato: `NOMBRE PRIMER_APELLIDO INICIAL_SEGUNDO_APELLIDO.`
// Ejemplos:
//   {name:"Pau",  primer_apellido:"De La Asunción", segundo_apellido:"Corva"}
//     → "Pau De La Asunción C."
//   {name:"Alba", primer_apellido:"", segundo_apellido:""} → "Alba"
// Se ignoran silenciosamente los valores nulos / vacíos.
export function studentShortLabel(s) {
  if (!s || typeof s !== "object") return "";
  const nombre = String(s.name || "").trim();
  const p1     = String(s.primer_apellido  || "").trim();
  const p2     = String(s.segundo_apellido || "").trim();
  const parts = [];
  if (nombre) parts.push(nombre);
  if (p1)     parts.push(p1);
  if (p2)     parts.push(p2.charAt(0).toUpperCase() + ".");
  return parts.join(" ");
}

/**
 * V1.XX · Etiqueta compacta para la vista MENSUAL del calendario:
 *   `Nombre + Inicial del primer apellido.`
 *   Ej. { name:"Pau", primer_apellido:"De La Asunción" } → "Pau D."
 * Fallback: si no hay primer apellido, sólo el nombre.
 */
export function studentCalendarLabelMonth(s) {
  if (!s || typeof s !== "object") return "";
  const nombre = String(s.name || "").trim();
  const p1     = String(s.primer_apellido  || "").trim();
  if (!nombre) return "";
  if (!p1) return nombre;
  return `${nombre} ${p1.charAt(0).toUpperCase()}.`;
}

/**
 * V1.XX · Etiqueta ampliada para las vistas AMPLIAS (día, semana, quincena
 * y panel del día): `Nombre + Primer apellido`.
 *   Ej. { name:"Pau", primer_apellido:"De La Asunción" } → "Pau De La Asunción"
 */
export function studentCalendarLabelWide(s) {
  if (!s || typeof s !== "object") return "";
  const nombre = String(s.name || "").trim();
  const p1     = String(s.primer_apellido  || "").trim();
  if (!nombre) return "";
  if (!p1) return nombre;
  return `${nombre} ${p1}`;
}

/**
 * V1.XX · Los tipos "examen_*" NO se cuentan como bloques de 45 min:
 * la franja de examen es libre y no consume bono. Este helper centraliza
 * la comprobación para ocultar el contador `(N)` en el calendario y en
 * las tarjetas de cita.
 */
// V1.69 · Paleta pastel para los bloqueos de agenda del profesor.
// «gris» conserva el formato actual (gris oscuro), pensado para el día completo.
export const UNAVAIL_COLORS = [
  { key: "gris", label: "Gris (por defecto)", swatch: "bg-neutral-700", chip: "bg-neutral-700 text-white", dot: "bg-neutral-400", muted: "text-neutral-300" },
  { key: "azul", label: "Azul", swatch: "bg-sky-200", chip: "bg-sky-100 text-sky-900 ring-1 ring-sky-300", dot: "bg-sky-500", muted: "text-sky-600" },
  { key: "verde", label: "Verde", swatch: "bg-emerald-200", chip: "bg-emerald-100 text-emerald-900 ring-1 ring-emerald-300", dot: "bg-emerald-500", muted: "text-emerald-600" },
  { key: "amarillo", label: "Amarillo", swatch: "bg-amber-200", chip: "bg-amber-100 text-amber-900 ring-1 ring-amber-300", dot: "bg-amber-500", muted: "text-amber-600" },
  { key: "naranja", label: "Naranja", swatch: "bg-orange-200", chip: "bg-orange-100 text-orange-900 ring-1 ring-orange-300", dot: "bg-orange-500", muted: "text-orange-600" },
  { key: "rosa", label: "Rosa", swatch: "bg-pink-200", chip: "bg-pink-100 text-pink-900 ring-1 ring-pink-300", dot: "bg-pink-500", muted: "text-pink-600" },
  { key: "turquesa", label: "Turquesa", swatch: "bg-teal-200", chip: "bg-teal-100 text-teal-900 ring-1 ring-teal-300", dot: "bg-teal-500", muted: "text-teal-600" },
];

export function unavailColor(color) {
  return UNAVAIL_COLORS.find((c) => c.key === (color || "gris")) || UNAVAIL_COLORS[0];
}

// V1.69 · Morado = examen práctico · Azul = examen teórico.
export function examTextClass(appointmentType) {
  if (appointmentType === "examen_practico") return "text-purple-700";
  if (appointmentType === "examen_teorico") return "text-blue-700";
  return "";
}

export function examBadgeClass(appointmentType) {
  if (appointmentType === "examen_practico") return "bg-purple-100 text-purple-800";
  if (appointmentType === "examen_teorico") return "bg-blue-100 text-blue-800";
  return "bg-neutral-100 text-neutral-700";
}

export function isExamAppointment(appointmentType) {
  return appointmentType === "examen_teorico" || appointmentType === "examen_practico";
}

// V1.64 · Toda cita se lee siempre igual: Inicio → Fin · Ruta.
// Si no hay punto de fin explícito se repite el de inicio (nunca se omite).
export function appointmentRouteLabel(appt) {
  if (!appt) return "";
  const start = String(appt.pickup_location || "").trim() || "—";
  const end = String(appt.pickup_location_end || "").trim() || start;
  const route = String(appt.circuit || "").trim();
  return `${start} → ${end}${route ? ` · ${route}` : ""}`;
}


const CLASS_MINUTES = 45;

function parseHHMM(s) {
  if (!s || typeof s !== "string") return null;
  const parts = s.split(":");
  if (parts.length !== 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function formatHHMM(totalMinutes) {
  const m = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/**
 * Compute the per-class breakdown for a session.
 * Each class lasts 45 minutes. Returns {numClasses, classes:[{index, time}]}.
 */
export function computeClassBreakdown(start, end) {
  const s = parseHHMM(start);
  const e = parseHHMM(end);
  if (s == null || e == null || e - s <= 0) {
    return { numClasses: 0, classes: [] };
  }
  const num = Math.max(1, Math.floor((e - s) / CLASS_MINUTES));
  const classes = Array.from({ length: num }, (_, i) => ({
    index: i + 1,
    time: formatHHMM(s + i * CLASS_MINUTES),
  }));
  return { numClasses: num, classes };
}

/**
 * Add N minutes to an HH:MM time. Returns HH:MM or empty string on bad input.
 */
export function addMinutes(time, minutes) {
  const t = parseHHMM(time);
  if (t == null) return "";
  return formatHHMM(t + minutes);
}

export function buildWhatsappLink({
  phone,
  name,
  date,
  time,
  endTime,
  location,
  locationEnd,
  confirmLink,
  type = "cita",
}) {
  let p = String(phone || "").replace(/\D/g, "");
  if (!p.startsWith("34")) p = "34" + p;

  const { numClasses, classes } = computeClassBreakdown(time, endTime);
  const intro =
    type === "recordatorio"
      ? `Hola, ${name}.\n\nTe recordamos tu sesión`
      : `Hola, ${name}.\n\nTienes una sesión`;
  const classesWord = numClasses === 1 ? "clase" : "clases";
  const endLoc = locationEnd && locationEnd !== location ? locationEnd : location;

  let body = `${intro} de ${numClasses} ${classesWord} el ${date} a las ${time}, que inicia en ${location} y finaliza en ${endLoc}.`;

  if (classes.length > 0) {
    body += `\n\nClases:`;
    for (const c of classes) {
      body += `\n${c.index}) ${c.time}`;
    }
    body += `\n\nHora fin: ${endTime}`;
  }

  body += `\n\n👉 CONFIRMAR CITA:\n${confirmLink}`;

  return `https://wa.me/${p}?text=${encodeURIComponent(body)}`;
}

export function publicConfirmLink(appointmentId) {
  const origin = window.location.origin;
  return `${origin}/confirm?id=${appointmentId}`;
}

export function formatDateES(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("es-ES", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}
